import React, { useEffect, useMemo, useState, useRef } from 'react';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';
import moment from 'moment';
import queryString from 'query-string';
import lodashCompact from 'lodash/fp/compact';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import { makeListingAddressStreetDisplay } from 'common/listingHelpers';
import { isPreLeaseActive } from 'common/preLeaseHelpers';
import { formatPhoneNumber } from 'common/formatters';
import {
  MARKETING_PARTNER_LISTING_INSTALL_STATUSES,
  MARKETING_PARTNER_LISTING_INSTALL_STATUS_COMPLETED,
  MARKETING_PARTNER_LISTING_INSTALL_STATUS_IN_PROGRESS,
  MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM,
} from 'common/marketingPartnerListingConstants';
import { LISTING_OCCUPANT_TYPE_TENANT, LISTING_OCCUPANT_TYPE_OWNER } from 'common/listingConstants';
import { JOB_STATUS_FAILED, JOB_STATUS_CANCELLED } from 'common/jobConstants';
import { USER_ROLE_ADMIN } from 'common/userRoles';

import useDispatch from 'client/hooks/useDispatch';

import SrIcon from 'client/components/SrIcon';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';
import SrSetupHealth from 'client/components/SrSetupHealth';
import SrStepsChecklist, {
  SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS,
  SR_STEPS_CHECKLIST_STATUS_COMPLETED,
  SR_STEPS_CHECKLIST_STATUS_FAILED,
} from 'client/components/SrStepsChecklist/SrStepsChecklist';
import SrTooltip, {
  SR_TOOLTIP_POSITION_LEFT,
  SR_TOOLTIP_ICON_QUESTION_MARK,
} from 'client/components/SrTooltip';
import SrCard, { SR_CARD_BULLET_COLOR_BLUE } from 'client/components/SrCard/SrCard';
import SrSeparator from 'client/components/SrSeparator';
import SrButton, {
  SR_BUTTON_HEIGHT_MEDIUM,
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_WHITE_BLUE_BORDER,
  SR_BUTTON_HEIGHT_SHORT,
} from 'client/components/SrButton/SrButton';
import SrPartnerMarketingItemsInstallModal from 'client/components/SrPartnerMarketingItemsInstallModal/SrPartnerMarketingItemsInstallModal';
import SrTag, {
  SR_TAG_THEME_BLUE_WHITE_SMALL,
  SR_TAG_THEME_LIGHTBLUE_BLACK,
  SR_TAG_THEME_LIGHTBLUE_BLACK_HOVER,
  SR_TAG_THEME_WHITE_BLACK,
} from 'client/components/SrTag/SrTag';
import SrAdminListingNotes from 'client/components/SrAdminListingNotes';
import SrModalV2, { MODAL_THEME_STACK } from 'client/components/SrModalV2/SrModalV2';

import './SrGoingToMarketLiveCard.less';

const installFailedStatuses = [JOB_STATUS_FAILED, JOB_STATUS_CANCELLED];

const formatPartnerListingDueDate = (date) => {
  if (!date) {
    return '';
  }

  return momentWithAustinTimezone(date).format('MMM D');
};

const getMarketingListingId = (location) => {
  const locationQuery = location.search ? queryString.parse(location.search) : null;

  return locationQuery && locationQuery.newMarketingListingId
    ? parseInt(locationQuery.newMarketingListingId, 10)
    : null;
};

/* eslint-disable max-len */
const getSteps = ({ marketingPartnerListing, setShowMarketingItemsInstallModal }) => {
  const isPreLease =
    marketingPartnerListing.preLease &&
    isPreLeaseActive({ preLease: marketingPartnerListing.preLease });
  const isReferralListing =
    marketingPartnerListing.listingAgreementSignedBy ===
    MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM;
  const propertyIsOccupied =
    marketingPartnerListing.occupantType === LISTING_OCCUPANT_TYPE_TENANT ||
    marketingPartnerListing.occupantType === LISTING_OCCUPANT_TYPE_OWNER;

  // Going-To-Market step display (true/false)
  const displaySunlockStatus =
    marketingPartnerListing.sunlockInstallJob || marketingPartnerListing.requireSunlock;
  const displayYardSignStatus =
    marketingPartnerListing.yardSignSetupJob || marketingPartnerListing.yardSignAllowed;
  const displayComboLockboxStatus = marketingPartnerListing.comboLockInstallJob;
  const displaySupraLockInstallStatus = marketingPartnerListing.supraLockInstallJob;
  const displayMarketingPartnerListingItems = !isPreLease;

  // Equipment setup/install failures (true/false)
  const getInstallFailureStatus = (status) => installFailedStatuses.includes(status);

  const sunlockInstallFailed = getInstallFailureStatus(
    marketingPartnerListing.sunlockInstallJob?.status,
  );
  const comboLockboxInstallFailed = getInstallFailureStatus(
    marketingPartnerListing.comboLockInstallJob?.status,
  );
  const supraLockInstallFailed = getInstallFailureStatus(
    marketingPartnerListing.supraLockInstallJob?.status,
  );
  const yardSignSetupFailed = getInstallFailureStatus(
    marketingPartnerListing.yardSignSetupJob?.status,
  );

  // Equipment setup/install successes (true/false)
  const getInstallSuccessStatus = (status) =>
    status === MARKETING_PARTNER_LISTING_INSTALL_STATUS_COMPLETED;

  const sunlockInstallComplete = getInstallSuccessStatus(
    marketingPartnerListing.sunlockInstallJob?.status,
  );
  const comboLockboxInstallComplete = getInstallSuccessStatus(
    marketingPartnerListing.comboLockInstallJob?.status,
  );
  const supraLockInstallComplete = getInstallSuccessStatus(
    marketingPartnerListing.supraLockInstallJob?.status,
  );
  const yardSignSetupComplete = getInstallSuccessStatus(
    marketingPartnerListing.yardSignSetupJob?.status,
  );

  // Determines when Equipment Install step is complete
  const noActiveOrders =
    !displaySunlockStatus &&
    !displayComboLockboxStatus &&
    !displaySupraLockInstallStatus &&
    !displayYardSignStatus;

  let equipmentInstallComplete = true;
  if (
    noActiveOrders ||
    (displaySunlockStatus && !sunlockInstallComplete) ||
    (displayComboLockboxStatus && !comboLockboxInstallComplete) ||
    (displaySupraLockInstallStatus && !supraLockInstallComplete) ||
    (displayYardSignStatus && !yardSignSetupComplete)
  ) {
    equipmentInstallComplete = false;
  }

  // Photography checklist statuses
  const displayPhotographyStatus =
    marketingPartnerListing.requirePhotos || marketingPartnerListing.requireVirtual3dTour;
  const photosApprovedBySunroom =
    marketingPartnerListing.photoLiveStatus === MARKETING_PARTNER_LISTING_INSTALL_STATUS_COMPLETED;

  const getEquipmentInstallCompletedDate = () => {
    const jobCompleteDates = [];
    if (displaySunlockStatus && sunlockInstallComplete) {
      jobCompleteDates.push(marketingPartnerListing.sunlockInstallJob.createdDate);
    }
    if (displayYardSignStatus && yardSignSetupComplete) {
      jobCompleteDates.push(marketingPartnerListing.yardSignSetupJob.createdDate);
    }
    if (displayComboLockboxStatus && comboLockboxInstallComplete) {
      jobCompleteDates.push(marketingPartnerListing.comboLockInstallJob.createdDate);
    }
    if (displaySupraLockInstallStatus && supraLockInstallComplete) {
      jobCompleteDates.push(marketingPartnerListing.supraLockInstallJob.createdDate);
    }

    if (jobCompleteDates.length > 0) {
      return Math.max(...jobCompleteDates.map((date) => new Date(date)));
    }
  };

  const keyAvailableExpectedAtListing = moment(
    marketingPartnerListing.dateKeyExpectedAtListing,
  ).format('MMM D');
  const marketingItemsKeyAvailableDate = marketingPartnerListing.dateKeyExpectedAtListing
    ? `(Key available on ${keyAvailableExpectedAtListing})`
    : '';
  const marketingItemsLabel = equipmentInstallComplete
    ? 'Marketing items installed'
    : 'Marketing items install';
  const displayPropertyConditionReport = marketingPartnerListing.requirePropertyConditionReport;
  const propertyConditionReportStatus =
    marketingPartnerListing.propertyConditionOverall &&
    marketingPartnerListing.propertyConditionUrl &&
    marketingPartnerListing.propertyConditionDateInspected
      ? MARKETING_PARTNER_LISTING_INSTALL_STATUS_COMPLETED
      : MARKETING_PARTNER_LISTING_INSTALL_STATUS_IN_PROGRESS;

  const steps = [
    {
      label: 'Submitted for marketing',
      status: MARKETING_PARTNER_LISTING_INSTALL_STATUS_COMPLETED,
      content:
        marketingPartnerListing.submittedForMarketingNotes ||
        marketingPartnerListing.partnerMarketingNotes ? ( // eslint-disable-line max-len
          <>
            {marketingPartnerListing.submittedForMarketingNotes && (
              <SrText
                font="greycliff"
                color="blueGreycliff"
                fontSize={14}
                lineHeight="20px"
                weight="500"
                wordWrap="break-word"
              >
                {marketingPartnerListing.submittedForMarketingNotes}
              </SrText>
            )}
            {marketingPartnerListing.partnerMarketingNotes && (
              <SrText
                font="greycliff"
                color="blueGreycliff"
                fontSize={14}
                lineHeight="20px"
                weight="500"
                display="block"
                wordWrap="break-word"
              >
                {marketingPartnerListing.partnerMarketingNotes}
              </SrText>
            )}
          </>
        ) : null,
      contentLeftPadding: true,
      sublabel: formatPartnerListingDueDate(marketingPartnerListing.createdDate),
    },
  ];

  if (isPreLease && marketingPartnerListing.preLease?.requireExteriorPhoto) {
    steps.push({
      label: 'External Photo',
      status:
        marketingPartnerListing.exteriorPhotographyJob?.status ===
        MARKETING_PARTNER_LISTING_INSTALL_STATUS_COMPLETED
          ? MARKETING_PARTNER_LISTING_INSTALL_STATUS_COMPLETED
          : MARKETING_PARTNER_LISTING_INSTALL_STATUS_IN_PROGRESS,
      contentLeftPadding: true,
      stepChecklistItems: [
        {
          label: 'Take photo',
          status: marketingPartnerListing.photographyJob?.photographyArrivedDate
            ? SR_STEPS_CHECKLIST_STATUS_COMPLETED
            : SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS,
        },
        {
          label: 'Upload photo',
          status: marketingPartnerListing.photoLiveDate
            ? SR_STEPS_CHECKLIST_STATUS_COMPLETED
            : SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS,
        },
      ],
    });
  }

  if (isReferralListing) {
    steps.push({
      label: marketingPartnerListing.listingAgreementSignedDate
        ? 'Landlord Completed Property Form'
        : 'Landlord Completing Property Form',
      status: marketingPartnerListing.listingAgreementSignedDate
        ? MARKETING_PARTNER_LISTING_INSTALL_STATUS_COMPLETED
        : MARKETING_PARTNER_LISTING_INSTALL_STATUS_IN_PROGRESS,
      content: !marketingPartnerListing.listingAgreementSignedDate && (
        <>
          <SrBox>
            <SrText
              font="greycliff"
              color="blueGreycliff"
              size="16"
              lineHeight="21px"
              letterSpacing="0"
              weight="500"
            >
              We've sent an invite to the landlord to submit the listing information.
            </SrText>
          </SrBox>
          {marketingPartnerListing.landlordPhone && marketingPartnerListing.landlordEmail && (
            <SrBox marginTop="10px">
              <SrText
                font="greycliff"
                color="blueGreycliff"
                size="16"
                lineHeight="21px"
                letterSpacing="0"
                weight="500"
                opacity="0.5"
                display="block"
              >
                Invites sent to:
              </SrText>
              <SrBox marginTop="4px">
                <SrText
                  font="greycliff"
                  color="blueGreycliff"
                  size="16"
                  lineHeight="21px"
                  letterSpacing="0"
                  weight="500"
                  display="block"
                >
                  {formatPhoneNumber(marketingPartnerListing.landlordPhone)}
                </SrText>
                <SrText
                  font="greycliff"
                  color="blueGreycliff"
                  size="16"
                  lineHeight="21px"
                  letterSpacing="0"
                  weight="500"
                  display="block"
                >
                  {marketingPartnerListing.landlordEmail}
                </SrText>
              </SrBox>
            </SrBox>
          )}
        </>
      ),
      contentLeftPadding: true,
      sublabel:
        marketingPartnerListing.listingAgreementSignedDate &&
        formatPartnerListingDueDate(marketingPartnerListing.listingAgreementSignedDate),
      completedDate: marketingPartnerListing.listingAgreementSignedDate,
    });
  }

  if (displayMarketingPartnerListingItems) {
    steps.push({
      label: `${marketingItemsLabel} ${marketingItemsKeyAvailableDate}`,
      status: equipmentInstallComplete
        ? MARKETING_PARTNER_LISTING_INSTALL_STATUS_COMPLETED
        : MARKETING_PARTNER_LISTING_INSTALL_STATUS_IN_PROGRESS,
      content:
        marketingPartnerListing.equipmentInstalledNotes + noActiveOrders
          ? 'Your client will choose what marketing items they would like installed. ' +
            `The items will be listed here.${
              marketingPartnerListing.equipmentInstalledNotes
                ? `\n\n${marketingPartnerListing.equipmentInstalledNotes}`
                : ''
            }`
          : marketingPartnerListing.equipmentInstalledNotes,
      contentLeftPadding: true,
      sublabel:
        equipmentInstallComplete && getEquipmentInstallCompletedDate()
          ? formatPartnerListingDueDate(getEquipmentInstallCompletedDate())
          : null,
      completedDate: equipmentInstallComplete && getEquipmentInstallCompletedDate(),
      stepChecklistItems: [
        ...(propertyIsOccupied
          ? [
              {
                label:
                  sunlockInstallComplete || comboLockboxInstallComplete || supraLockInstallComplete
                    ? 'Contacted occupant to schedule installation'
                    : 'Contact occupant to schedule installation',
                status:
                  sunlockInstallComplete || comboLockboxInstallComplete || supraLockInstallComplete
                    ? SR_STEPS_CHECKLIST_STATUS_COMPLETED
                    : SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS,
              },
            ]
          : []),
        ...(displaySunlockStatus
          ? [
              {
                label:
                  equipmentInstallComplete || sunlockInstallComplete
                    ? 'Sunlock Installed'
                    : 'Install Sunlock',
                status:
                  equipmentInstallComplete || sunlockInstallComplete
                    ? SR_STEPS_CHECKLIST_STATUS_COMPLETED
                    : sunlockInstallFailed
                    ? SR_STEPS_CHECKLIST_STATUS_FAILED
                    : SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS,
              },
            ]
          : []),
        ...(displayComboLockboxStatus
          ? [
              {
                label:
                  equipmentInstallComplete || comboLockboxInstallComplete
                    ? 'Combo Lockbox Installed'
                    : 'Install Combo Lockbox',
                status:
                  equipmentInstallComplete || comboLockboxInstallComplete
                    ? SR_STEPS_CHECKLIST_STATUS_COMPLETED
                    : comboLockboxInstallFailed
                    ? SR_STEPS_CHECKLIST_STATUS_FAILED
                    : SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS,
              },
            ]
          : []),
        ...(displaySupraLockInstallStatus
          ? [
              {
                label:
                  equipmentInstallComplete || supraLockInstallComplete
                    ? 'Supra Lockbox Installed'
                    : 'Install Supra Lockbox',
                status:
                  equipmentInstallComplete || supraLockInstallComplete
                    ? SR_STEPS_CHECKLIST_STATUS_COMPLETED
                    : supraLockInstallFailed
                    ? SR_STEPS_CHECKLIST_STATUS_FAILED
                    : SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS,
              },
            ]
          : []),
        ...(displayYardSignStatus
          ? [
              {
                label:
                  equipmentInstallComplete || yardSignSetupComplete
                    ? 'Yard Sign Installed'
                    : 'Install Yard Sign',
                status:
                  equipmentInstallComplete || yardSignSetupComplete
                    ? SR_STEPS_CHECKLIST_STATUS_COMPLETED
                    : yardSignSetupFailed
                    ? SR_STEPS_CHECKLIST_STATUS_FAILED
                    : SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS,
              },
            ]
          : []),
      ],
      stepChecklistButton: !noActiveOrders && {
        buttonText: 'See how these items work',
        onClick: () => setShowMarketingItemsInstallModal(true),
      },
      alwaysShowChecklistItems: true,
    });
  }

  if (displayPropertyConditionReport) {
    steps.push({
      label: 'Property Condition Report',
      status: propertyConditionReportStatus,
      contentLeftPadding: true,
      stepChecklistItems: [],
    });
  }

  if (displayPhotographyStatus) {
    steps.push({
      label: 'Professional Photos',
      status: photosApprovedBySunroom
        ? MARKETING_PARTNER_LISTING_INSTALL_STATUS_COMPLETED
        : MARKETING_PARTNER_LISTING_INSTALL_STATUS_IN_PROGRESS,
      content: marketingPartnerListing.photoLiveNotes,
      contentLeftPadding: true,
      sublabel: formatPartnerListingDueDate(marketingPartnerListing.photoLiveDate),
      completedDate: marketingPartnerListing.photoLiveDate,
      stepChecklistItems: lodashCompact([
        {
          label: photosApprovedBySunroom ? 'Arrived at property' : 'Arrive at property',
          status: photosApprovedBySunroom
            ? SR_STEPS_CHECKLIST_STATUS_COMPLETED
            : SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS,
        },
        marketingPartnerListing.requirePhotos && {
          label: photosApprovedBySunroom ? 'Took photos' : 'Take photos',
          status: photosApprovedBySunroom
            ? SR_STEPS_CHECKLIST_STATUS_COMPLETED
            : SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS,
        },
        marketingPartnerListing.requirePhotos && {
          label: photosApprovedBySunroom ? 'Edited photos' : 'Edit photos',
          status: photosApprovedBySunroom
            ? SR_STEPS_CHECKLIST_STATUS_COMPLETED
            : SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS,
        },
        marketingPartnerListing.requirePhotos && {
          label: photosApprovedBySunroom ? 'Uploaded photos' : 'Upload photos',
          status: photosApprovedBySunroom
            ? SR_STEPS_CHECKLIST_STATUS_COMPLETED
            : SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS,
        },
        marketingPartnerListing.requireVirtual3dTour && {
          label: '3D tour',
          status: photosApprovedBySunroom
            ? SR_STEPS_CHECKLIST_STATUS_COMPLETED
            : SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS,
        },
      ]),
      alwaysShowChecklistItems: true,
    });
  }

  if (!displayPropertyConditionReport) {
    steps.sort((stepA, stepB) => {
      const statusIndexA = MARKETING_PARTNER_LISTING_INSTALL_STATUSES.indexOf(stepA.status);
      const statusIndexB = MARKETING_PARTNER_LISTING_INSTALL_STATUSES.indexOf(stepB.status);
      if (statusIndexA !== statusIndexB) {
        return statusIndexA - statusIndexB;
      }
      if (stepA.status !== MARKETING_PARTNER_LISTING_INSTALL_STATUS_COMPLETED) {
        return 0;
      }
      return moment(stepA.completedDate).unix() - moment(stepB.completedDate).unix();
    });
  }

  steps.push({
    label: 'Listing live',
    status: MARKETING_PARTNER_LISTING_INSTALL_STATUS_IN_PROGRESS,
    content: marketingPartnerListing.listingLiveNotes,
    contentLeftPadding: true,
    stepChecklistItems: [
      {
        label: marketingPartnerListing.description
          ? 'Marketing description written'
          : 'Write marketing description',
        status: marketingPartnerListing.description
          ? SR_STEPS_CHECKLIST_STATUS_COMPLETED
          : SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS,
      },
      {
        label: 'Optimize listing for renter searches',
      },
      {
        label: 'Spotlight best photos',
      },
      {
        label: 'Validate marketing items',
      },
      {
        label: 'Account Manager sign-off',
      },
      {
        label: 'Syndicate to 3rd-party sites',
      },
      {
        label: 'Kickoff renter marketing',
      },
      {
        label: 'Feature listing on Sunroom',
      },
    ],
    alwaysShowChecklistItems: true,
  });

  return steps;
};
/* eslint-enable max-len */

const NotesTag = ({ dispatch, reloadData, currentUser, marketingPartnerListing }) => {
  const [isListingNotesModalOpen, setIsListingModalOpen] = useState(null);

  const isAdmin = currentUser.roles.includes(USER_ROLE_ADMIN);
  const hasNotes = marketingPartnerListing.notesCount > 0;
  const blueTheme = isAdmin ? SR_TAG_THEME_LIGHTBLUE_BLACK_HOVER : SR_TAG_THEME_LIGHTBLUE_BLACK;
  const theme = hasNotes ? blueTheme : SR_TAG_THEME_WHITE_BLACK;

  const onTagClick = (e) => {
    e.preventDefault();

    if (isAdmin) {
      setIsListingModalOpen(true);
    }
  };

  const onCloseNotesModal = () => {
    if (reloadData) {
      reloadData(marketingPartnerListing);
    }
    setIsListingModalOpen(false);
  };

  return (
    <>
      <SrTag theme={theme} variation="goingToMarketCard" onClick={onTagClick}>
        {`${pluralize('Note', marketingPartnerListing.notesCount, true)}`}
      </SrTag>
      {isListingNotesModalOpen && (
        <SrModalV2
          closeOnBackgroundClick
          showCloseButton={false}
          contentMaxWidth="546px"
          contentPadding="0px"
          theme={MODAL_THEME_STACK}
          onClose={onCloseNotesModal}
        >
          <SrCard header="Notes (Admin-Only)" color={SR_CARD_BULLET_COLOR_BLUE} maxWidth="none">
            <SrAdminListingNotes
              listingId={marketingPartnerListing.listing.id}
              dispatch={dispatch}
              currentUser={currentUser}
            />
          </SrCard>
        </SrModalV2>
      )}
    </>
  );
};

const SrGoingToMarketLiveCard = ({
  marketingPartnerListing,
  currentUser,
  onEdit,
  onEditWalkThroughFields,
  onEditMarketingPartnerListingDueDate,
  location,
  appUrl,
  hideEdit,
  hideEditDueDate,
  hideReportDelay,
  reloadData,
}) => {
  const dispatch = useDispatch();
  const isAdmin = currentUser.roles.includes(USER_ROLE_ADMIN);
  const newMarketingListingRef = useRef(null);
  const [newMarketingListingId, setMarketingPartnerListingId] = useState(null);
  const [showMarketingItemsInstallModal, setShowMarketingItemsInstallModal] = useState(false);
  const [collapseCard, setCollapseCard] = useState(isAdmin);

  useEffect(() => {
    if (location) {
      setMarketingPartnerListingId(getMarketingListingId(location));
    }
  }, [location]);

  const isNewestMarketingListing = newMarketingListingId === marketingPartnerListing.id;
  const isPreLease =
    marketingPartnerListing.preLease &&
    isPreLeaseActive({ preLease: marketingPartnerListing.preLease });

  if (isNewestMarketingListing) {
    newMarketingListingRef.current.scrollIntoView({ behavior: 'smooth' });
  }

  const SrCardOrSrBox = isAdmin ? SrLink : SrBox;

  const dueDateUpdatesText = marketingPartnerListing.dueDateUpdates.map((dueDateUpdate) => {
    return (
      <SrText key={dueDateUpdate.id} size={13} lineHeight="13px">
        {dueDateUpdate.userName || 'API'} {' on '}
        {momentWithAustinTimezone(dueDateUpdate.createdDate).format('MMM D, h:mma')}:&nbsp; From{' '}
        {momentWithAustinTimezone(dueDateUpdate.oldValue).format('MMM D')}&nbsp; to{' '}
        {momentWithAustinTimezone(dueDateUpdate.newValue).format('MMM D')}
        <br />
      </SrText>
    );
  });

  const steps = useMemo(() => {
    const memoSteps = getSteps({
      marketingPartnerListing,
      setShowMarketingItemsInstallModal,
    });

    return memoSteps;
  }, [marketingPartnerListing, setShowMarketingItemsInstallModal]);

  const _shouldDisplaySetupHealth = () => {
    return (
      marketingPartnerListing.setupHealth &&
      isAdmin &&
      (isPreLease ? !!marketingPartnerListing.preleaseCompletedDate : true)
    );
  };

  const _getSetupHealthComponent = () => {
    return (
      <SrSetupHealth
        setupHealth={marketingPartnerListing.setupHealth}
        listingId={marketingPartnerListing.listingId}
      />
    );
  };

  const shouldDisplaySetupHealth = _shouldDisplaySetupHealth();
  const setupHealthComponent = _getSetupHealthComponent();

  return (
    <>
      <span ref={newMarketingListingRef} style={{ scrollMargin: '140px', minWidth: '0' }}>
        <SrCard
          noContentPadding
          padding="0 0 25px"
          styleName={isNewestMarketingListing ? 'highlightAnimation' : null}
        >
          {marketingPartnerListing.propertyManager && (
            <SrFlex bgColor="orange200" align="center" justify="between" padding="15px 20px">
              <SrFlex align="center">
                <SrIcon className="person-filled-orange" marginRight={8} />
                <SrText weight="bold" color="orange300">
                  {marketingPartnerListing.propertyManager.name}
                </SrText>
              </SrFlex>
            </SrFlex>
          )}
          <SrBox padding="23px 20px 0">
            <SrFlex justify="between">
              <SrCardOrSrBox
                to={`/admin/listings/edit/${marketingPartnerListing.listingId}`}
                target="_blank"
              >
                {isAdmin && marketingPartnerListing.listing?.status === 'Needs Review' && (
                  <SrText font="greycliff" color="red" size="14" display="block" weight="800">
                    NEEDS REVIEW
                  </SrText>
                )}
                {isAdmin && marketingPartnerListing.partner && (
                  <SrText font="greycliff" size="16" color="blue300" display="block">
                    {marketingPartnerListing.partner.displayName}
                  </SrText>
                )}
                <SrText font="greycliff" fontSize="21px" weight="800" letterSpacing="0">
                  {makeListingAddressStreetDisplay(marketingPartnerListing.listing)}
                </SrText>
                {isAdmin && marketingPartnerListing.marketDisplayName && (
                  <SrText font="greycliff" color="grey" size="12" display="block">
                    {marketingPartnerListing.marketDisplayName}
                  </SrText>
                )}
              </SrCardOrSrBox>
              {!hideEdit && (
                <SrText
                  font="greycliff"
                  weight="800"
                  color="blue300"
                  cursor="pointer"
                  onClick={onEdit}
                >
                  Edit
                </SrText>
              )}
            </SrFlex>
            <SrFlex marginTop="8px">
              {marketingPartnerListing.dueDateUpdates.length > 0 && isAdmin && (
                <SrBox margin="2px 4px 0 0">
                  <SrTooltip
                    position={SR_TOOLTIP_POSITION_LEFT}
                    iconName={SR_TOOLTIP_ICON_QUESTION_MARK}
                    tooltipContentStyle={{ width: '328px' }}
                  >
                    {dueDateUpdatesText}
                  </SrTooltip>
                </SrBox>
              )}
              <SrText font="greycliff" color="green" weight="medium" letterSpacing="0">
                {marketingPartnerListing.dueDate &&
                  `Goes live on or before ${formatPartnerListingDueDate(
                    marketingPartnerListing.dueDate,
                  )}`}
                {isAdmin && marketingPartnerListing.dueDate && !hideEditDueDate && (
                  <SrBox display="inlineBlock" marginLeft="9px">
                    <SrText
                      color="blue300"
                      font="greycliff"
                      weight="bolder"
                      cursor="pointer"
                      onClick={onEditMarketingPartnerListingDueDate}
                    >
                      Edit
                    </SrText>
                  </SrBox>
                )}
              </SrText>
            </SrFlex>
            <SrFlex marginTop="8px" gap="6px">
              {marketingPartnerListing.preLease && (
                <SrTag theme={SR_TAG_THEME_BLUE_WHITE_SMALL} variation="preLease">
                  PRE-LEASE
                </SrTag>
              )}

              <NotesTag
                dispatch={dispatch}
                marketingPartnerListing={marketingPartnerListing}
                isAdmin={isAdmin}
                reloadData={reloadData}
                currentUser={currentUser}
              />
            </SrFlex>
          </SrBox>
          <SrSeparator marginTop="20px" marginBottom="10px" />
          <SrBox padding="0 20px">
            <SrText font="greycliff" weight="800" letterSpacing="0">
              What Sunroom does next:
            </SrText>
            <SrText
              paddingLeft="5px"
              font="greycliff"
              fontSize="16px"
              cursor="pointer"
              decoration="underline"
              color="grey"
              weight="600"
              onClick={() => setCollapseCard(!collapseCard)}
            >
              {collapseCard ? 'see more' : 'see less'}
            </SrText>
            <SrBox marginTop="13px" />
            {!collapseCard && <SrStepsChecklist steps={steps} />}
            {!isPreLease && (
              <SrBox marginTop="24px">
                <SrLink to={`${appUrl}/learn-after-market-property`} target="_blank">
                  <SrButton
                    theme={SR_BUTTON_THEME_ADOBE_WHITE_BLUE_BORDER}
                    heightType={SR_BUTTON_HEIGHT_SHORT}
                    borderColor="#007AFF"
                  >
                    Learn More
                  </SrButton>
                </SrLink>
              </SrBox>
            )}
            {isAdmin && !hideReportDelay && !isPreLease && (
              <SrBox marginTop="23px">
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_BLUE}
                  hasBoxShadow
                  minWidth="100%"
                  heightType={SR_BUTTON_HEIGHT_MEDIUM}
                  onClick={onEditWalkThroughFields}
                >
                  Report Setup Delay
                </SrButton>
              </SrBox>
            )}
            {shouldDisplaySetupHealth && (
              <>
                <SrSeparator marginTop="20px" marginBottom="10px" />
                <SrBox marginTop="15px">{setupHealthComponent}</SrBox>
              </>
            )}
          </SrBox>
        </SrCard>
      </span>
      {showMarketingItemsInstallModal && (
        <SrPartnerMarketingItemsInstallModal
          onClose={() => setShowMarketingItemsInstallModal(false)}
          includesSunlock={marketingPartnerListing.requireSunlock}
          includesYardSign={marketingPartnerListing.yardSignAllowed !== false}
          includesComboLock={marketingPartnerListing.comboLockInstallJob}
        />
      )}
    </>
  );
};

SrGoingToMarketLiveCard.propTypes = {
  marketingPartnerListing: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onEditMarketingPartnerListingDueDate: PropTypes.func.isRequired,
  appUrl: PropTypes.string.isRequired,
  onEditWalkThroughFields: PropTypes.func.isRequired,
  location: PropTypes.object,
  hideEdit: PropTypes.bool,
  hideEditDueDate: PropTypes.bool,
  hideReportDelay: PropTypes.bool,
  reloadData: PropTypes.func,
};

SrGoingToMarketLiveCard.displayName = 'SrGoingToMarketLiveCard';

export default SrGoingToMarketLiveCard;
